export type RuntimeConfig = {
  audioInitialBufferMs: number;
  outputPrimeOnConnect: boolean;
  carStartTestEnabled: boolean;
  availableVoiceRuntimes: VoiceRuntime[];
  openAIRealtimeVoices: string[];
  voiceRuntimeAvailabilityKnown: boolean;
};


export type AuthUser = {
  sub: string;
  email: string;
  name?: string | null;
  picture?: string | null;
};

export type AuthSession = {
  authenticated: boolean;
  user?: AuthUser;
};

export type AuthConfig = {
  configured: boolean;
  googleClientId?: string | null;
};

export type InteractionMode = "voice" | "text";
export type StartupMode = "normal" | "car";
export type VoiceRuntime = "gemini_live" | "openai_realtime" | "openai_realtime_mini";

export function isVoiceRuntime(value: unknown): value is VoiceRuntime {
  return value === "gemini_live" || value === "openai_realtime" || value === "openai_realtime_mini";
}

export function isOpenAIRealtimeRuntime(
  value: unknown
): value is Extract<VoiceRuntime, "openai_realtime" | "openai_realtime_mini"> {
  return value === "openai_realtime" || value === "openai_realtime_mini";
}

export type ServerConnectOptions = {
  voice: string;
  sessionId: string;
  mode?: InteractionMode;
  startupMode?: StartupMode;
  forceNew?: boolean;
  voiceRuntime?: VoiceRuntime;
  openAIRealtimeVoice?: string;
  reasoningEffort?: "none" | "low" | "medium" | "high";
};

export type TranscriptEvent = {
  text: string;
  finished?: boolean;
};

export type ToolStartEvent = {
  callId: string;
  blocking: boolean;
  name: string;
  args?: Record<string, unknown>;
};

export type ToolResultEvent = {
  callId: string;
  name: string;
  status: "ok" | "error" | "cancelled";
  error?: string;
  summary?: string;
  result?: unknown;
};

export type ActionStateEvent = {
  state: "idle" | "awaiting_confirmation" | "executing" | "executed" | "canceled" | "outcome_unknown" | "inspected";
  action?: string;
  message?: string;
  confirmationPrompt?: string;
  confirmationDetails?: string;
};

export type StatusEvent = {
  message: string;
};

export type TextResponseEvent = {
  text: string;
  finished?: boolean;
};

export type ContextUsageEvent = {
  source: "gemini" | "estimate";
  promptTokenCount?: number | null;
  responseTokenCount?: number | null;
  totalTokenCount: number;
  contextWindowTokens: number;
  compressionTriggerTokens: number;
  percentUsed: number;
};

export type SessionConfigEvent = {
  voiceRuntime: VoiceRuntime;
  modelId?: string | null;
  reasoningEffort?: "none" | "low" | "medium" | "high" | null;
};

export type WebSource = {
  id: string;
  title: string;
  url: string;
  snippet?: string | null;
};

export type WebSourcesEvent = {
  status: "ok" | "no_results" | "unavailable" | "rate_limited" | "timeout" | "error";
  query: string;
  answer?: string | null;
  message?: string | null;
  sources: WebSource[];
};

export type SessionEndState = "wrapping_up" | "saving" | "saved" | "save_failed";

export type SessionEndEvent = {
  state: SessionEndState;
  message: string;
};
